import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/* ── Split tags string ("hvac, repair, tips") into an array ── */
function parseTags(raw = "") {
  if (!raw) return [];
  return raw.split(",").map((t) => t.trim()).filter(Boolean);
}

/* ── First N words of a string, with ellipsis if truncated ── */
function truncateWords(raw = "", count = 15) {
  if (!raw) return "";
  const words = raw.trim().split(/\s+/);
  if (words.length <= count) return raw.trim();
  return words.slice(0, count).join(" ") + "…";
}

/* ── Strip protocol/trailing slash for a clean SERP-style URL display ── */
function formatDisplayUrl(fullUrl) {
  if (!fullUrl) return "";
  return fullUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

/* ── Render plain-text content as paragraphs (preserves blank-line breaks) ── */
function ContentBody({ content }) {
  if (!content) return null;
  const paragraphs = content.split(/\r?\n\r?\n/).filter((p) => p.trim());
  return (
    <div className="bd-content">
      {paragraphs.map((p, i) => (
        <p key={i}>{p.trim()}</p>
      ))}
    </div>
  );
}

function Breadcrumb({ title }) {
  return (
    <nav className="bd-bread" aria-label="Breadcrumb">
      <a href="/" className="bd-bread__link">Home</a>
      <span className="bd-bread__sep" aria-hidden="true">›</span>
      <a href="/blog" className="bd-bread__link">Blog</a>
      <span className="bd-bread__sep" aria-hidden="true">›</span>
      <span className="bd-bread__current">{title}</span>
    </nav>
  );
}

/* ── Inline gallery using image2 / image3 / image4 (whichever exist) ── */
function SideGallery({ images }) {
  if (!images.length) return null;
  return (
    <div className="bd-gallery">
      {images.map((img, i) => (
        <div key={i} className="bd-gallery__item">
          <img src={`${UPLOADS_URL}/blogs/${img}`} alt="" />
        </div>
      ))}
    </div>
  );
}

function ShareLinks({ url, title }) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  return (
    <div className="bd-share">
      <span className="bd-share__label">Share:</span>
      <a
        className="bd-share__btn"
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
        target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M16 8.05A8 8 0 1 0 6.5 16V10.1H4.5V8.05h2V6.3c0-2 1.2-3.1 3-3.1.86 0 1.76.15 1.76.15v1.93h-.99c-.98 0-1.27.6-1.27 1.23v1.54h2.17l-.35 2.05H9v5.9A8 8 0 0 0 16 8.05Z"/></svg>
      </a>
      <a
        className="bd-share__btn"
        href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
        target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M16 3.04c-.59.26-1.22.43-1.88.51a3.28 3.28 0 0 0 1.44-1.81c-.63.37-1.34.64-2.08.79A3.27 3.27 0 0 0 7.86 5.6c0 .31.04.62.1.91A9.3 9.3 0 0 1 1.1 2.86a3.27 3.27 0 0 0 1.01 4.36 3.26 3.26 0 0 1-1.48-.41v.04a3.27 3.27 0 0 0 2.62 3.21 3.3 3.3 0 0 1-1.47.06 3.27 3.27 0 0 0 3.05 2.27A6.56 6.56 0 0 1 0 13.8 9.27 9.27 0 0 0 5.03 15.2c6.04 0 9.34-5 9.34-9.34l-.01-.42A6.7 6.7 0 0 0 16 3.04Z"/></svg>
      </a>
      <a
        className="bd-share__btn"
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M0 1.5C0 .67.67 0 1.5 0h13A1.5 1.5 0 0 1 16 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13ZM4.94 13.45V6h-2.5v7.45h2.5ZM3.69 4.92c.87 0 1.41-.58 1.41-1.3-.02-.74-.54-1.3-1.39-1.3-.85 0-1.41.56-1.41 1.3 0 .72.54 1.3 1.39 1.3ZM13.45 13.45v-4.1c0-2.2-1.17-3.22-2.74-3.22-1.26 0-1.83.7-2.14 1.18v-1.01h-2.5c.03.7 0 7.15 0 7.15h2.5V9.27c0-.4.03-.8.18-1.08.16-.28.42-.57.93-.57.65 0 .92.5.92 1.22v4.61h2.85Z"/></svg>
      </a>
    </div>
  );
}

/* ── Sidebar block: "More Articles" list ── */
function MoreArticlesSidebar({ posts }) {
  if (!posts.length) return null;
  return (
    <div className="bd-side__block">
      <h3 className="bd-side__heading">More Articles</h3>
      <ul className="bd-side__list">
        {posts.map((p) => (
          <li key={p.id}>
            <a href={`/blog/${p.slug}`} className="bd-side__item">
              {p.image1 ? (
                <img
                  src={`${UPLOADS_URL}/blogs/${p.image1}`}
                  alt={p.title}
                  className="bd-side__item-img"
                />
              ) : (
                <div className="bd-side__item-img bd-side__item-img--placeholder" />
              )}
              <div className="bd-side__item-body">
                <h4 className="bd-side__item-title">{p.title}</h4>
                {p.published_at && (
                  <span className="bd-side__item-date">{formatDate(p.published_at)}</span>
                )}
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Sidebar block: SEO / Search preview card for meta_title + meta_description ── */
function SeoPreview({ metaTitle, metaDescription, fallbackTitle, fallbackExcerpt, url }) {
  const title = metaTitle || fallbackTitle;
  const description = metaDescription || fallbackExcerpt;
  if (!title && !description) return null;

  return (
    <div className="bd-side__block bd-seo">
      <h3 className="bd-side__heading">
        <span className="bd-seo__heading-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M11 11L15 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </span>
        Search Preview
      </h3>
      <p className="bd-seo__caption">How this post appears on Google</p>
      <div className="bd-seo__card">
        <div className="bd-seo__url-row">
          <span className="bd-seo__favicon" aria-hidden="true" />
          <span className="bd-seo__url">{formatDisplayUrl(url)}</span>
        </div>
        {title && <p className="bd-seo__title">{truncateWords(title, 12)}</p>}
        {description && <p className="bd-seo__desc">{truncateWords(description, 28)}</p>}
      </div>
    </div>
  );
}

function Specialities({ services }) {
  if (!services.length) return null;
  return (
    <div className="bd-spec">
      <div className="bd-spec__inner">
        <h2 className="bd-spec__heading">Our Specialities</h2>
        <div className="bd-spec__grid">
          {services.map((svc) => (
            <div key={svc.id} className="bd-spec__card">
              <div className="bd-spec__img-wrap">
                {svc.image4 ? (
                  <img
                    src={`${UPLOADS_URL}/services/${svc.image4}`}
                    alt={svc.title}
                    className="bd-spec__img"
                  />
                ) : (
                  <div className="bd-spec__img bd-spec__img--placeholder" />
                )}
                <div className="bd-spec__icon">
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 0a6 6 0 0 0-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 0 0-6-6Zm0 8.5A2.5 2.5 0 1 1 8 3.5a2.5 2.5 0 0 1 0 5Z"/>
                  </svg>
                </div>
              </div>
              <div className="bd-spec__body">
                <h3 className="bd-spec__title">{svc.title}</h3>
                <p className="bd-spec__desc">{truncateWords(svc.description, 15)}</p>
                <a href={`/services/${svc.slug}`} className="bd-spec__link">
                  Read More
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlogDetailPage() {
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchJSON(`${API_BASE}/blog-detail.php?slug=${encodeURIComponent(slug)}`),
      fetchJSON(`${API_BASE}/blogs.php`).catch(() => []),
      fetchJSON(`${API_BASE}/specialities.php`).catch(() => []),
    ])
      .then(([postData, allPosts, specData]) => {
        if (cancelled) return;
        setPost(postData?.post || postData || null);
        const list = Array.isArray(allPosts) ? allPosts : [];
        setRelated(list.filter((p) => p.slug !== slug).slice(0, 5));
        setSpecialities(Array.isArray(specData) ? specData : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("BlogDetailPage load error:", err);
        setError("Failed to load blog post.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return null;

  if (error || !post) return (
    <div className="bd-error">
      <h2>Post not found</h2>
      <p>The blog post you are looking for does not exist or has been removed.</p>
      <a href="/blog" className="bd-error__link">Back to Blog</a>
    </div>
  );

  const tags = parseTags(post.tags);
  const galleryImages = [post.image2, post.image3, post.image4].filter(Boolean);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <>
      <div className="bd">

        {/* ── HERO ── */}
        <div className="bd-hero">
          {post.image1 && (
            <img
              src={`${UPLOADS_URL}/blogs/${post.image1}`}
              alt={post.title}
              className="bd-hero__img"
            />
          )}
          <div className="bd-hero__overlay" />
          <div className="bd-hero__content">
            <Breadcrumb title={post.title} />
            {post.category && <span className="bd-hero__category">{post.category}</span>}
            <h1 className="bd-hero__title">{post.title}</h1>
            <div className="bd-hero__meta">
              {post.author && <span>By {post.author}</span>}
              {post.published_at && (
                <>
                  <span className="bd-hero__dot" aria-hidden="true">•</span>
                  <span>{formatDate(post.published_at)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── BODY: main content + sidebar ── */}
        <div className="bd-body">
          <div className="bd-body__inner">

            <main className="bd-main">
              {post.excerpt && <p className="bd-excerpt">{post.excerpt}</p>}

              <ContentBody content={post.content} />

              <SideGallery images={galleryImages} />

              <div className="bd-footer-row">
                {tags.length > 0 && (
                  <div className="bd-tags">
                    {tags.map((tag) => (
                      <span key={tag} className="bd-tags__item">#{tag}</span>
                    ))}
                  </div>
                )}
                <ShareLinks url={shareUrl} title={post.title} />
              </div>
            </main>

            <aside className="bd-side">
              <MoreArticlesSidebar posts={related} />
              <SeoPreview
                metaTitle={post.meta_title}
                metaDescription={post.meta_description}
                fallbackTitle={post.title}
                fallbackExcerpt={post.excerpt}
                url={shareUrl}
              />
            </aside>

          </div>
        </div>

        {/* ── SPECIALITIES ── */}
        <Specialities services={specialities} />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

          .bd *, .bd *::before, .bd *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .bd a { text-decoration: none; }
          .bd ul { list-style: none; }
          .bd { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          .bd-error { text-align: center; padding: 80px 24px; font-family: 'Roboto', sans-serif; color: #424244; }
          .bd-error h2 { font-size: 28px; color: #013d97; margin-bottom: 12px; }
          .bd-error p  { margin-bottom: 24px; }
          .bd-error__link { display: inline-block; padding: 10px 24px; background: #029add; color: #fff; border-radius: 6px; font-weight: 700; }

          .bd-hero { position: relative; height: 320px; overflow: hidden; background: #013d97; }
          .bd-hero__img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
          .bd-hero__overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(1,21,70,.92) 10%, rgba(1,61,151,.45) 80%); }
          .bd-hero__content { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; gap: 10px; padding: 32px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0; }
          .bd-hero__category {
            display: inline-block; align-self: flex-start;
            background: #e96c29; color: #fff; font-size: 11px; font-weight: 800;
            letter-spacing: .05em; text-transform: uppercase; padding: 5px 12px; border-radius: 3px;
          }
          .bd-hero__title { font-size: clamp(24px, 3.2vw, 40px); font-weight: 900; color: #fff; line-height: 1.2; max-width: 800px; }
          .bd-hero__meta { display: flex; align-items: center; gap: 8px; font-size: 13px; color: rgba(255,255,255,.8); font-weight: 600; }
          .bd-hero__dot { color: rgba(255,255,255,.5); }

          .bd-bread { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
          .bd-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); }
          .bd-bread__link:hover { color: #fff; }
          .bd-bread__sep { color: rgba(255,255,255,.5); font-size: 14px; }
          .bd-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }

          /* ── BODY LAYOUT: main + sidebar ── */
          .bd-body { padding: 56px 0; }
          .bd-body__inner {
            max-width: 1280px; margin: 0 auto; padding: 0 40px;
            display: grid; grid-template-columns: 1fr 340px;
            gap: 32px; align-items: start;
          }

          .bd-main { background: #fff; padding: 40px; border-radius: 6px; box-shadow: 0 2px 14px rgba(0,0,0,.06); }
          .bd-excerpt { font-size: 17px; font-weight: 600; color: #1a1a2e; line-height: 1.6; margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #eef0f5; }

          .bd-content { display: flex; flex-direction: column; gap: 18px; }
          .bd-content p { font-size: 15px; color: #424244; line-height: 1.85; text-align: justify; }

          .bd-gallery { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; margin: 32px 0; }
          .bd-gallery__item { border-radius: 4px; overflow: hidden; aspect-ratio: 4/3; }
          .bd-gallery__item img { width: 100%; height: 100%; object-fit: cover; display: block; }

          .bd-footer-row {
            display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;
            gap: 16px; margin-top: 32px; padding-top: 24px; border-top: 1px solid #eef0f5;
          }
          .bd-tags { display: flex; gap: 8px; flex-wrap: wrap; }
          .bd-tags__item { font-size: 12.5px; font-weight: 700; color: #029add; background: #f0f8fd; padding: 6px 12px; border-radius: 14px; }

          .bd-share { display: flex; align-items: center; gap: 10px; }
          .bd-share__label { font-size: 13px; font-weight: 700; color: #5a5f72; }
          .bd-share__btn {
            width: 32px; height: 32px; border-radius: 50%; background: #013d97; color: #fff;
            display: flex; align-items: center; justify-content: center; transition: background .2s;
          }
          .bd-share__btn:hover { background: #029add; }

          /* ── SIDEBAR ── */
          .bd-side { display: flex; flex-direction: column; gap: 24px; }
          .bd-side__block {
            background: #fff; border-radius: 6px; box-shadow: 0 2px 14px rgba(0,0,0,.06);
            padding: 26px;
          }
          .bd-side__heading {
            display: flex; align-items: center; gap: 8px;
            font-size: 17px; font-weight: 800; color: #1a1a2e;
            margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #eef0f5;
          }
          .bd-side__list { display: flex; flex-direction: column; gap: 16px; }
          .bd-side__item { display: flex; align-items: flex-start; gap: 12px; }
          .bd-side__item-img { width: 72px; height: 60px; flex-shrink: 0; border-radius: 4px; object-fit: cover; display: block; }
          .bd-side__item-img--placeholder { background: linear-gradient(135deg, #013d97, #029add); }
          .bd-side__item-body { display: flex; flex-direction: column; gap: 4px; }
          .bd-side__item-title { font-size: 13.5px; font-weight: 700; color: #1a1a2e; line-height: 1.4; transition: color .2s; }
          .bd-side__item:hover .bd-side__item-title { color: #029add; }
          .bd-side__item-date { font-size: 11.5px; color: #8890a0; }

          /* ── SEO / SEARCH PREVIEW CARD ── */
          .bd-seo { padding-bottom: 22px; }
          .bd-seo__heading-icon { display: inline-flex; color: #029add; }
          .bd-seo__caption { font-size: 12px; color: #8890a0; margin-top: -10px; margin-bottom: 14px; }
          .bd-seo__card {
            background: #fff; border: 1px solid #e3e6ec; border-radius: 8px;
            padding: 16px 18px; font-family: arial, sans-serif;
          }
          .bd-seo__url-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
          .bd-seo__favicon {
            width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
            background: linear-gradient(135deg, #013d97, #029add);
          }
          .bd-seo__url { font-size: 12.5px; color: #202124; }
          .bd-seo__title { font-size: 17px; color: #1a0dab; line-height: 1.3; margin-bottom: 4px; }
          .bd-seo__desc { font-size: 12.5px; color: #4d5156; line-height: 1.5; }

          /* ── SPECIALITIES ── */
          .bd-spec { background: #fff; padding: 64px 0 80px; border-top: 1px solid #eef0f5; }
          .bd-spec__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }
          .bd-spec__heading { font-size: clamp(24px, 2.8vw, 34px); font-weight: 900; color: #1a1a2e; text-align: center; margin-bottom: 36px; }
          .bd-spec__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }

          .bd-spec__card {
            background: #f9fafb; border: 1px solid #eef0f5; border-radius: 8px; overflow: hidden;
            transition: transform .25s, box-shadow .25s;
          }
          .bd-spec__card:hover { transform: translateY(-4px); box-shadow: 0 10px 28px rgba(0,0,0,.1); }

          .bd-spec__img-wrap { position: relative; aspect-ratio: 4/3; overflow: hidden; background: #e6f0fb; }
          .bd-spec__img { width: 100%; height: 100%; object-fit: cover; display: block; }
          .bd-spec__img--placeholder { background: linear-gradient(135deg, #013d97, #029add); }
          .bd-spec__icon {
            position: absolute; left: 20px; bottom: -22px;
            width: 48px; height: 48px; border-radius: 50%;
            background: #013d97; color: #fff;
            display: flex; align-items: center; justify-content: center;
            border: 3px solid #fff; box-shadow: 0 4px 12px rgba(0,0,0,.18);
          }

          .bd-spec__body { padding: 36px 22px 24px; display: flex; flex-direction: column; gap: 10px; }
          .bd-spec__title { font-size: 18px; font-weight: 800; color: #1a1a2e; }
          .bd-spec__desc { font-size: 13.5px; color: #6b7280; line-height: 1.65; }
          .bd-spec__link {
            display: inline-flex; align-items: center; gap: 6px;
            font-size: 12.5px; font-weight: 800; color: #e96c29;
            letter-spacing: .03em; margin-top: 4px;
          }
          .bd-spec__card:hover .bd-spec__link { color: #c85a1f; }

          @media (max-width: 1024px) {
            .bd-body__inner { grid-template-columns: 1fr; }
            .bd-side { order: -1; }
            .bd-spec__grid { grid-template-columns: repeat(2, 1fr); }
          }
          @media (max-width: 700px) {
            .bd-hero { height: 260px; }
            .bd-hero__content { padding: 20px 20px; }
            .bd-body__inner { padding: 0 20px; }
            .bd-main { padding: 24px; }
            .bd-spec__inner { padding: 0 20px; }
            .bd-spec__grid { grid-template-columns: 1fr; }
            .bd-footer-row { flex-direction: column; align-items: flex-start; }
          }

          a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
          @media (prefers-reduced-motion: reduce) { .bd *, .bd *::before, .bd *::after { transition-duration: 0ms !important; } }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default BlogDetailPage;